// データセットとデータ変換操作の型定義

import type { ColumnName, DataValue, DataSchema } from './core.types';
import type { ColumnId, DatasetId, TransformOperationId } from './id.types';
import type { FilterExpression } from './expression.types';

// core.typesからDataSchemaを再エクスポート
export type { DataSchema } from './core.types';
// id.typesから必要な型を再エクスポート
export type { ColumnId, DatasetId } from './id.types';

// 除外された行のデータ
export interface ExcludedRow {
  data: Record<ColumnId, DataValue>; // RowNumber列も含む全列データ
  comment?: string;
  excludedAt: string;
}

// 行のコメント
export interface RowComment {
  comment: string;
  commentedAt: string;
}

// 原始データセット（ファイルから直接読み込まれたデータ）
export interface PrimaryDataSet {
  id: DatasetId; // データセットの一意識別子（ウィンドウ管理にも使用）
  name: string; // データセット名（ユーザー定義）
  schema: DataSchema;
  rowCount: number; // データの行数
  type: 'primary';
  
  /** 
   * @deprecated 🚨 DO NOT ACCESS DIRECTLY! Use DatasetAccessor instead.
   * Direct access to data property is unsafe and may cause column ID/name mismatch.
   */
  data: Record<ColumnId, DataValue[]>; // 列指向データ格納（列IDをキーとして使用）
  
  // 除外管理
  exclusions?: {
    // RowNumber値 -> 除外データ
    excludedRows: Record<number, ExcludedRow>;
  };

  // コメント（除外/非除外問わず、RowNumber値をキーとする）
  comments?: Record<number, RowComment>;
  
  metadata: {
    // データセットメタデータ
    originalFileName: string;
    importedAt: string;
    fileSize?: number;
    description?: string;

    // URLソース関連
    sourceUrl?: string;       // URLから読み込んだ場合のURL
    sourceType?: 'file' | 'url';  // ソースの種類
    lastFetchedAt?: string;   // URLから最後に取得した日時
  };
}

// データ変換操作の定義
export interface DataTransformOperation {
  id: TransformOperationId;
  type: string;
  description?: string;
}

// SQLクエリ操作
export interface SQLQueryOperation extends DataTransformOperation {
  type: 'sql_query';
  query: string; // SQLクエリ文字列
  targetDatasetId: DatasetId; // 対象データセットID
}

// Crosstab/Pivot ソート方式
export type CrosstabSortMethod = 'label-asc' | 'label-desc' | 'value';

// Crosstab/Pivot ソート順序
export type CrosstabSortOrder = 'desc' | 'asc';

// Crosstab/Pivot ソート設定（行または列）
export interface CrosstabSortSetting {
  method: CrosstabSortMethod;
  sortByKey?: string; // valueソート時にどの列/行でソートするか（'total' or 列/行のキー）
  order?: CrosstabSortOrder; // valueソート時の順序
}

// Crosstab/Pivot ソート設定
export interface CrosstabSortConfig {
  rows?: CrosstabSortSetting;
  columns?: CrosstabSortSetting;
}

// Crosstab/Pivot 値フィールド設定
export interface CrosstabValueField {
  field: ColumnName;
  aggregation: string; // 集計関数 (sum, avg, count, etc.)
  alias?: string;
}

// Crosstab/Pivot操作
export interface CrosstabOperation extends DataTransformOperation {
  type: 'crosstab';
  targetDatasetId: DatasetId; // 対象データセットID
  rowFields: ColumnName[]; // 行グループ化フィールド
  columnFields: ColumnName[]; // 列グループ化フィールド
  valueFields: CrosstabValueField[]; // 集計対象フィールド配列
  sortConfig?: CrosstabSortConfig; // ソート設定
  options: {
    showRowTotals: boolean;
    showColumnTotals: boolean;
    showGrandTotal: boolean;
  };
}

// Filter操作
export interface FilterOperation extends DataTransformOperation {
  type: 'filter';
  targetDatasetId: DatasetId; // フィルタ対象のデータセット
  filterExpression: FilterExpression; // フィルタ条件
}

// GLM診断操作
export interface GLMDiagnosticsOperation extends DataTransformOperation {
  type: 'glm_diagnostics';
  modelId: string; // 診断対象のGLMモデルID
}

// GLM予測操作
export interface GLMPredictionOperation extends DataTransformOperation {
  type: 'glm_prediction';
  modelId: string; // 予測に使用するGLMモデルID
  targetDatasetId: DatasetId; // 予測対象のデータセット
  confidenceLevels: number[]; // 信頼区間の水準（例: [90, 95, 99]）
  predictionLevels: number[]; // 予測区間の水準（例: [90, 95, 99]）
}

// Dummy Coding操作
export interface DummyCodingOperation extends DataTransformOperation {
  type: 'dummy_coding';
  targetDatasetId: DatasetId; // 対象データセットID
  encodings: Array<{
    originalColumnId: string;
    originalColumnName: string;
    categories: string[];
    referenceCategory: string;
    dummyVariableNames: string[];
  }>;
}

// すべての変換操作のユニオン型
export type DataTransformOperationUnion =
  | SQLQueryOperation
  | CrosstabOperation
  | FilterOperation
  | GLMDiagnosticsOperation
  | GLMPredictionOperation
  | DummyCodingOperation;

// 派生データセット（単一の操作によって定義される）
export interface DerivedDataSet {
  id: DatasetId; // データセットの一意識別子（ウィンドウ管理にも使用）
  name: string; // データセット名（ユーザー定義）
  schema: DataSchema; // 出力スキーマ（操作適用後の列構成）
  rowCount?: number; // データの行数（キャッシュされている場合）
  type: 'derived';
  parentDatasetIds: DatasetId[]; // 親データセットのID配列（参照のみ）
  operation: DataTransformOperationUnion; // 適用される操作（単一）

  // キャッシュされた結果データ（集計結果等）
  // デフォルトではシリアライズから除外される（__transient__マーカーによる）
  // materializedがtrueの場合のみMDSファイルに保存される
  data?: Record<ColumnId, DataValue[]>;

  // Materialized View設定
  // trueの場合、dataプロパティをMDSファイルに保存する
  //
  // 注意: __transient__マーカーとは別にこのプロパティを持つ理由:
  // - __transient__マーカーはMDS保存時に除外されるため、状態が保持されない
  // - dataプロパティの存在で判定すると、評価時にdataが生成された時点で
  //   意図せずMaterializedと判定されてしまう
  // - materializedプロパティはMDSに保存され、読み込み時に正しく復元される
  materialized?: boolean;

  // 実行時評価用のメタデータ
  evaluationMetadata?: {
    lastEvaluated?: string;
    rowCount?: number;
    estimatedSize?: number;
  };

  // コメント（RowNumber値をキーとする）
  comments?: Record<number, RowComment>;

  // 除外行（型変換エラーなど、操作の結果として除外された行）
  exclusions?: {
    excludedRows?: Record<number, ExcludedRow>;
  };

  metadata: {
    // データセットメタデータ
    createdAt: string;
    description?: string;
    basedOn: DatasetId[]; // 元となったデータセットのID配列
  };
}

// 一時的なデータセット（表示専用、永続化されない）
export interface EphemeralDataSet {
  id: DatasetId; // データセットの一意識別子
  name: string; // データセット名（表示用）
  schema: DataSchema; // データスキーマ
  rowCount: number; // データの行数
  type: 'ephemeral';

  // 必須のデータプロパティ（遅延評価不要）
  data: Record<ColumnId, DataValue[]>;

  metadata?: {
    // 一時データセットのメタデータ
    createdAt?: string;
    purpose?: 'selected_rows' | 'filtered_view' | 'temporary_analysis';
    description?: string;

    // フィルタ結果の場合のメタデータ
    sourceDatasetId?: DatasetId; // フィルタ元のデータセットID
    filterExpression?: FilterExpression; // フィルタ条件
    additionalFilterExpression?: FilterExpression; // FilteredDataTabで追加されたフィルタ条件
  };
}

// データセットのユニオン型
// Phase 2以降: EphemeralDataSetも一時的にプロジェクトに追加できるようにするため、DataSetに含める
// 永続化（MDS保存）の際はEphemeralDataSetを除外する
export type DataSet = PrimaryDataSet | DerivedDataSet | EphemeralDataSet;

// データを持つことが保証されたデータセット型
// DatasetAccessorなど、データに直接アクセスする必要がある場合に使用
// この型を要求することで、呼び出し側がデータの存在を保証する責務を持つ
export type DatasetWithData =
  | PrimaryDataSet
  | EphemeralDataSet
  | (DerivedDataSet & { data: Record<ColumnId, DataValue[]> });

/**
 * データセットがdataを持っているか判定する型ガード関数
 *
 * @param dataset - チェック対象のデータセット
 * @returns dataを持つ場合true（型がDatasetWithDataに絞り込まれる）
 *
 * @example
 * ```typescript
 * if (!hasData(dataset)) {
 *   // DerivedDataSetでdataがない場合は再評価
 *   dataset = await materializeDerivedDataset(dataset);
 * }
 * // この時点でdatasetはDatasetWithData型
 * const accessor = new DatasetAccessor(dataset);
 * ```
 */
export function hasData(dataset: DataSet): dataset is DatasetWithData {
  // PrimaryDataSetとEphemeralDataSetは常にdataを持つ
  if (dataset.type === 'primary' || dataset.type === 'ephemeral') {
    return true;
  }
  // DerivedDataSetはdataがある場合のみtrue
  return dataset.data !== undefined;
}

// データセット評価関連のユーティリティ型

// データセット評価結果
export interface DataSetEvaluationResult {
  success: boolean;
  data?: Record<ColumnName, DataValue[]>;
  schema?: DataSchema;
  rowCount?: number;
  error?: string;
  evaluationTime?: number; // ミリ秒
}

// データセット評価オプション
export interface DataSetEvaluationOptions {
  maxRows?: number; // 最大行数制限
  columnSubset?: ColumnName[]; // 評価する列の制限
  cacheResults?: boolean; // 結果をキャッシュするか
  forceRefresh?: boolean; // キャッシュを無視して再評価
}

// データセットの依存関係グラフ（循環参照検出用）
export interface DataSetDependency {
  datasetId: DatasetId;
  dependencies: DatasetId[]; // 依存するデータセットのID
  depth: number; // 依存の深さ
}
